import { useQuery } from '@tanstack/react-query';
import { contactRate, conversionRate } from '@/lib/metrics';
import { dailySeries, fetchKpiWindow, type KpiWindow, type Metric } from '@/hooks/useHomeWeekMetrics';

/**
 * The portal home's four figures (W5, B3-ClientDashboard, DES-11/19): RDV
 * obtenus, Appels, Taux de contact (connected / calls), Conversion (meetings /
 * connected), over the last 30 days, with the trend against the 30 days
 * before.
 *
 * Server aggregates (CEO-10): POST /api/analytics/kpi, the route the dialer
 * home reads. For a viewer the server narrows every count to its own
 * customers (viewerCustomerScope); `customerId` narrows further (the agency's
 * « voir comme le client » preview), never widens. Metric contracts of W2:
 * connected = reached the agent, meeting = the meeting flag.
 */

const DAY = 86_400_000;
export const PORTAL_KPI_DAYS = 30;

export interface PortalKpis {
  meetings: Metric;
  calls: Metric;
  contact: Metric;
  conversion: Metric;
  series: { calls: number[]; meetings: number[] };
}

/** `previous` is ignored when it holds no call at all: no trend from nothing. */
export function buildPortalKpis(current: KpiWindow, previous: KpiWindow | null, from: number, to: number): PortalKpis {
  const prev = previous && previous.total > 0 ? previous : null;
  return {
    meetings: { current: current.meetings, previous: prev ? prev.meetings : null, kind: 'count' },
    calls: { current: current.total, previous: prev ? prev.total : null, kind: 'count' },
    contact: {
      current: contactRate(current.connected, current.total),
      previous: prev ? contactRate(prev.connected, prev.total) : null,
      kind: 'rate',
    },
    conversion: {
      current: conversionRate(current.meetings, current.connected),
      previous: prev ? conversionRate(prev.meetings, prev.connected) : null,
      kind: 'rate',
    },
    series: {
      calls: dailySeries(current.byDay, from, to, 'calls'),
      meetings: dailySeries(current.byDay, from, to, 'meetings'),
    },
  };
}

export function usePortalKpis(customerId: string | null = null, enabled = true) {
  return useQuery({
    queryKey: ['portal-home-kpi', customerId, PORTAL_KPI_DAYS],
    queryFn: async () => {
      const to = Date.now();
      const from = to - PORTAL_KPI_DAYS * DAY;
      const [current, previous] = await Promise.all([
        fetchKpiWindow(from, to, customerId),
        fetchKpiWindow(from - PORTAL_KPI_DAYS * DAY, from, customerId),
      ]);
      return buildPortalKpis(current, previous, from, to);
    },
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}
